import React, { useEffect, useState, useMemo, ReactElement } from 'react';
import Header from './generic/Header';
import { Footer } from './generic/Footer';
import { useTenant } from './TenantContext';
import { Control, TextControl } from '../type';
import WhatYouShouldKnow from './WhatYouShouldKnow';
import GTMConsent from './GTMConsent';
import { INLINE_LINK_CLASS, FAFSA_URL } from '../utils/linkUtils';


// Simple i18n utility (replace with your actual i18n solution)
const t = (key: string) => {
    const translations: Record<string, string> = {
        'welcome.title': "Welcome to HCE's NPC!",
        'welcome.startButton': 'Start Net Price Calculator',
        'welcome.subtitle': 'Net Price Calculator',
        // Add more keys as needed
    };
    return translations[key] || key;
};



const makeInlineLink = (key: string, href: string, children: React.ReactNode, target = '_blank', rel = 'noopener noreferrer'): ReactElement => (
    <a key={key} href={href} target={target} rel={rel} className={INLINE_LINK_CLASS}>
        {children}
    </a>
);

// Define a configuration interface for optional props
interface WelcomePageConfig {
    welcomeTitle?: string;
}

// Update props to include configuration object
interface WelcomePageProps {
    onStartClick: () => void;
    controls: Control[];
    config?: WelcomePageConfig; // NEW config prop
}

const WelcomePage = ({ onStartClick, controls, config }: WelcomePageProps) => {
    const { tenantConfig, getAttributesByEntityName } = useTenant();
    const [welcomePageContent, setWelcomePageContent] = useState<Control[]>([]);

    const bannerTitle = `Welcome to ${tenantConfig?.authority?.displayName ?? ''}'s NPC!`;

    // Capture primaryColor from tenantConfig (from API response)
    const primaryColor = tenantConfig?.authority?.authorityBranding?.primaryColor;

    // Derive contactUrl directly from tenantConfig so it updates reactively when tenant data loads.
    const contactUrl = useMemo(() => {
        const raw = tenantConfig?.authority?.authorityBranding?.headerLink ?? '';
        let parsed: { key: string; value: string }[] = [];
        try { parsed = JSON.parse(raw); } catch { parsed = []; }
        const entry = parsed.find((l) => l.key === 'Contact');
        const val = entry?.value ?? localStorage.getItem('CONTACT_URL') ?? '';
        const urlMatch = /(https?:\/\/[^\s]{1,2000})/.exec(val);
        return urlMatch ? urlMatch[1] : val;
    }, [tenantConfig?.authority?.authorityBranding?.headerLink]);

    // Use static data instead of API call
    useEffect(() => {
        const welcomeAttributes = getAttributesByEntityName('Welcome');

        const transformAttributeToControl = (attribute: any): Control => {
            const baseControl = {
                _id: attribute._id,
                controlId: attribute._id,
                controlName: attribute.name,
                name: attribute.name,
                displayName: attribute.friendlyName ?? attribute.name,
                dataType: attribute.dataType,
                sequence: attribute.order,
                visible: true,
                onclauseEnable: false,
                required: attribute.isRequired ?? false,
                screenName: "Welcome",
                tenant: "default",
                description: attribute.description,
                friendlyName: attribute.friendlyName,
                isRequired: attribute.isRequired ?? false,
                isMultivalued: attribute.isMultivalued ?? false,
                tooltipMessage: attribute.toolTip,
                toolTip: attribute.toolTip,
                possibleValuesSource: attribute.possibleValuesSource,
                possibleValues: attribute.possibleValues ? [attribute.possibleValues] : undefined,
                possibleMinValue: attribute.possibleMinValue,
                possibleMaxValue: attribute.possibleMaxValue,
                possibleRangeStep: attribute.possibleRangeStep,
                enablingAttribute: attribute.enablingAttribute,
                enablingValue: attribute.enablingValue,
                defaultValue: attribute.defaultValue,
            };

            // Since these are informational text attributes, use TextControl with label type
            return {
                ...baseControl,
                controltype: "label",
                staticValue: attribute.description,
            } as TextControl;
        };

        const transformedData = welcomeAttributes.map(transformAttributeToControl);
        setWelcomePageContent(transformedData);

    }, [getAttributesByEntityName]);

    const replacePlaceholders = (text: string) => {
        if (!text) return '';
        let newText = text;
        if (tenantConfig?.authority?.displayName) {
            newText = newText.replaceAll(/\[ClientName\]/g, tenantConfig.authority.displayName);
        }

        return newText;
    };

    const parseHtmlLinkAttributes = (attributes: string, href: string): Record<string, string> => {
        const attributeRegex = /(\w{1,100})\s{0,10}=\s{0,10}(?:"([^"]{0,2000})"|'([^']{0,2000})')/g;
        const parsed: Record<string, string> = { href };
        let attrMatch;
        while ((attrMatch = attributeRegex.exec(attributes)) !== null) {
            const attrName = attrMatch[1].toLowerCase();
            const attrValue = attrMatch[2] ?? attrMatch[3] ?? '';
            if (attrName !== 'href') {
                parsed[attrName] = attrValue;
            }
        }
        return parsed;
    };

    const parseTextWithLinks = (text: string): (string | ReactElement)[] => {
        if (!text) return [text];

        // Step 1: Process FAFSA text first (before HTML links)
        let processedText = text;

        // Replace "Free Application for Federal Student Aid" with a placeholder
        const fafsaRegex = /Free Application for Federal Student Aid(\s*\(FAFSA®?\))?/gi;
        const fafsaMatches: { text: string; index: number }[] = [];
        let match;

        while ((match = fafsaRegex.exec(processedText)) !== null) {
            fafsaMatches.push({
                text: match[0],
                index: match.index
            });
        }

        // Replace FAFSA text with placeholders (in reverse order to maintain indices)
        // Iterate in reverse, but use the original index in fafsaMatches for placeholder naming
        for (let i = fafsaMatches.length - 1; i >= 0; i--) {
            const fafsaMatch = fafsaMatches[i];
            const placeholder = `__FAFSA_PLACEHOLDER_${i}__`;
            processedText = processedText.slice(0, fafsaMatch.index) +
                placeholder +
                processedText.slice(fafsaMatch.index + fafsaMatch.text.length);
        }

        // Step 2: Process HTML links
        // Safe regex: match <a> tags generically to avoid ReDoS-vulnerable [^>]*...[^>]* patterns.
        const linkRegex = /<a\b([^>]*)>([\s\S]*?)<\/a>/gi;
        const parts: (string | ReactElement)[] = [];
        let lastIndex = 0;
        let elementKey = 0;

        while ((match = linkRegex.exec(processedText)) !== null) {
            // Add text before the link (may contain FAFSA placeholders)
            if (match.index > lastIndex) {
                const beforeText = processedText.slice(lastIndex, match.index);
                if (beforeText) {
                    const beforeParts = replaceFAFSAPlaceholders(beforeText, fafsaMatches, elementKey);
                    parts.push(...beforeParts);
                    elementKey += beforeParts.filter(part => typeof part !== 'string').length;
                }
            }

            // Extract href and link text from matched tag
            const fullMatch = match[0];
            const attributes = match[1];
            const linkText = match[2];

            // Extract href safely using alternation to avoid mixed-quote false positives
            const hrefMatch = /\bhref\s*=\s*(?:"([^"]*)"|'([^']*)')/i.exec(attributes);
            if (!hrefMatch) {
                lastIndex = match.index + fullMatch.length;
                continue;
            }
            const href = hrefMatch[1] ?? hrefMatch[2] ?? '';

            // Parse additional attributes using safe alternation for quote types
            const parsedAttributes = parseHtmlLinkAttributes(attributes, href);

            // Create React link element for existing HTML
            const linkElement = makeInlineLink(
                `html-link-${elementKey++}`,
                href,
                linkText,
                parsedAttributes.target || '_blank',
                parsedAttributes.rel || 'noopener noreferrer'
            );

            parts.push(linkElement);
            lastIndex = match.index + fullMatch.length;
        }

        // Handle remaining text after HTML links
        if (lastIndex < processedText.length) {
            const remainingText = processedText.slice(lastIndex);
            if (remainingText) {
                const remainingParts = replaceFAFSAPlaceholders(remainingText, fafsaMatches, elementKey);
                parts.push(...remainingParts);
            }
        }

        // If no HTML links were found, process the entire text for FAFSA placeholders
        if (parts.length === 0) {
            return replaceFAFSAPlaceholders(processedText, fafsaMatches, 0);
        }

        return parts;
    };

    // Helper function to replace FAFSA placeholders with hyperlinks and linkify plain URLs
    const replaceFAFSAPlaceholders = (text: string, fafsaMatches: { text: string; index: number }[], startKey: number): (string | ReactElement)[] => {
        if (!text) return [text];

        const parts: (string | ReactElement)[] = [];
        let remainingText = text;
        let keyCounter = startKey;

        // Process FAFSA placeholders first
        if (fafsaMatches.length > 0) {
            fafsaMatches.forEach((fafsaMatch, index) => {
                const placeholder = `__FAFSA_PLACEHOLDER_${index}__`;
                const placeholderIndex = remainingText.indexOf(placeholder);

                if (placeholderIndex !== -1) {
                    // Add text before placeholder (linkify URLs in this part)
                    if (placeholderIndex > 0) {
                        const beforeText = remainingText.slice(0, placeholderIndex);
                        const beforeParts = linkifyPlainUrls(beforeText, keyCounter);
                        parts.push(...beforeParts);
                        keyCounter += beforeParts.filter(part => typeof part !== 'string').length;
                    }

                    // Add FAFSA hyperlink
                    const fafsaLink = makeInlineLink(
                        `fafsa-link-${keyCounter++}`,
                        FAFSA_URL,
                        fafsaMatch.text
                    );
                    parts.push(fafsaLink);

                    // Update remaining text
                    remainingText = remainingText.slice(placeholderIndex + placeholder.length);
                }
            });
        }

        // Linkify any remaining text (URLs)
        if (remainingText) {
            const remainingParts = linkifyPlainUrls(remainingText, keyCounter);
            parts.push(...remainingParts);
        }

        return parts.length > 0 ? parts : [text];
    };

    // Helper function to linkify plain URLs in text
    const linkifyPlainUrls = (text: string, startKey: number): (string | ReactElement)[] => {
        if (!text) return [text];

        // Regex to identify URLs starting with http, https, or www.
        const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/g;
        const parts: (string | ReactElement)[] = [];
        let lastIndex = 0;
        let keyCounter = startKey;
        let match;

        while ((match = urlRegex.exec(text)) !== null) {
            // Add text before the URL
            if (match.index > lastIndex) {
                parts.push(text.slice(lastIndex, match.index));
            }

            // Add the URL as a link
            const url = match[0];
            const href = url.startsWith('www.') ? `https://${url}` : url;
            parts.push(makeInlineLink(`url-link-${keyCounter++}`, href, url));

            lastIndex = match.index + url.length;
        }

        // Add any remaining text after the last URL
        if (lastIndex < text.length) {
            parts.push(text.slice(lastIndex));
        }

        return parts.length > 0 ? parts : [text];
    };

    return (
        <>
            <GTMConsent />
            <div className='flex flex-col min-h-screen'>
                <Header lbl={tenantConfig?.authority?.name ?? ''} />
                <main className='container mx-auto flex-grow w-full flex flex-col justify-center items-center'>
                    <div className='flex flex-col items-center p-8 w-full' style={{ "marginTop": "100px", "marginBottom": "100px" }}>
                        <h1
                            className="font-bold text-[40px] tracking-[0.15px] align-middle text-left w-full pt-5 pb-9"
                            style={{
                                fontFamily: 'Open Sans, sans-serif',
                                color: primaryColor
                            }}
                        >
                            {t('welcome.subtitle')}
                        </h1>
                        <div className='grid grid-cols-1 md:grid-cols-2 gap-8 w-full items-start'>
                            <div className='bg-white shadow-lg rounded-lg relative overflow-hidden'>
                                <div
                                    style={{ backgroundColor: primaryColor }}
                                    className='text-white font-bold py-2 text-left px-6'
                                >
                                    {/* Use the configurable bannerTitle */}
                                    {bannerTitle}
                                </div>
                                <div className='p-6 overflow-y-auto max-h-64'>
                                    {welcomePageContent.map((ctrl) => {
                                        const processedText = replacePlaceholders(ctrl.description);
                                        const textWithLinks = parseTextWithLinks(processedText);

                                        return (
                                            <p key={ctrl._id || ctrl.name || crypto.randomUUID()} className='mt-2 text-gray-600'>
                                                {textWithLinks.map((part, index) => {
                                                    if (typeof part === 'string') {
                                                        // Replace both escaped \n and actual newlines with <br> tags for proper line breaks
                                                        const textWithLineBreaks = part.replace(/\\n/g, '<br>').replace(/\n/g, '<br>');
                                                        if (textWithLineBreaks.includes('<br>')) {
                                                            return <span key={`${ctrl._id}-text-${part.slice(0, 20)}-${index}`} dangerouslySetInnerHTML={{ __html: textWithLineBreaks }} />;
                                                        }
                                                        return part;
                                                    }
                                                    return React.cloneElement(part, { key: `${ctrl._id}-link-${index}` });
                                                })}
                                            </p>
                                        );
                                    })}
                                </div>
                                <div className='p-4 border-t flex flex-col sm:flex-row justify-between items-center gap-4'>
                                    <button
                                        className='text-blue-600 hover:underline focus:outline-none focus:ring-2 focus:ring-blue-500 underline underline-offset-1'
                                        onClick={() => {
                                            if (contactUrl) {
                                                const newWin = window.open(contactUrl, '_blank', 'noopener,noreferrer');
                                                if (newWin) {
                                                    try { newWin.opener = null; } catch (e) { console.error('Failed to clear opener reference:', e); }
                                                }
                                            } else {
                                                console.error('Contact URL is not configured. Please contact your administrator.');
                                            }
                                        }}
                                    >
                                        Contact Us
                                    </button>
                                    <button
                                        className="font-semibold py-2 px-8 rounded transition-all duration-200"
                                        onClick={onStartClick}
                                        style={{
                                            backgroundColor: tenantConfig?.authority?.authorityBranding?.secondaryColor ?? "",
                                            color: tenantConfig?.authority?.authorityBranding?.accentColor ?? "",
                                            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.6)'
                                        }}
                                        onMouseEnter={(e) => {
                                            e.currentTarget.style.backgroundColor = tenantConfig?.authority?.authorityBranding?.primaryColor ?? '';
                                            e.currentTarget.style.color = tenantConfig?.authority?.authorityBranding?.accentColor ?? '';
                                            e.currentTarget.style.textDecoration = 'underline';
                                        }}
                                        onMouseLeave={(e) => {
                                            e.currentTarget.style.backgroundColor = tenantConfig?.authority?.authorityBranding?.secondaryColor ?? '';
                                            e.currentTarget.style.color = tenantConfig?.authority?.authorityBranding?.accentColor ?? '';
                                            e.currentTarget.style.textDecoration = 'none';
                                        }}
                                    >
                                        {t('welcome.startButton')}
                                    </button>
                                </div>
                            </div>
                            <WhatYouShouldKnow faqName="Welcome - FAQ" />
                        </div>
                    </div>
                </main>
                <Footer />
            </div>
        </>
    );
};

export default WelcomePage;